import React, { useEffect, useState, useRef } from 'react';
import { Box, Grid, Paper, Typography, IconButton, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useParams } from 'react-router-dom';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import api from '../apis/api';
import { useApp } from '../context/AppContext';
import { SAMPLE_AVATARS, getAvatarPreviewUrl, getAvatarImageUrl } from '../utils/avatars';

export default function AvatarPage() {
  const { userDetails } = useApp();
  const [avatars, setAvatars] = useState([]);
  const [orgAvatarId, setOrgAvatarId] = useState(null);
  const params = useParams();
  const routeOrgId = params?.id || params?.orgId || null;
  const [loading, setLoading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [iframeSrcPreview, setIframeSrcPreview] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [search, setSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAvatar, setPendingAvatar] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        // Use shared avatar list from utils
        if (!cancelled) setAvatars(SAMPLE_AVATARS);

        // load organization to determine selected avatar (kept from original implementation)
        try {
          const orgId = routeOrgId || userDetails?.orgId || userDetails?.organizationId || userDetails?.organization?._id;
          if (orgId) {
            const orgRes = await api.get(`/organization/${orgId}`, { headers: { 'x-access-token': `${userDetails?.jwtToken || ''}` } });
            const org = orgRes?.data?.organization || orgRes?.data || null;
            if (!cancelled && org) setOrgAvatarId(org.avatar_id || null);
          }
        } catch (orgErr) {
          console.warn('Failed to load organization info', orgErr);
        }

        /*
        // Original API fetch (commented out per request) -- do not remove
        const res = await api.get('/heygen/avatars', {
          headers: { 'x-access-token': `${userDetails?.jwtToken || ''}` },
        });

        const payload = res && res.data;
        const parsed = (payload && payload.data && payload.data.avatars) || payload?.data || [];
        if (!cancelled) setAvatars(parsed);
        */
      } catch (e) {
        console.error('Failed to load avatars', e);
        if (!cancelled) setSnack({ open: true, message: 'Failed to load avatars', severity: 'error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [userDetails]);

  const playPreview = (avatar) => {
    // Attempt to use iframe embed if present (Vimeo/other embed)
    const iframeHtml = avatar.preview_video_url || avatar.iframe_embed || avatar.vimeo_iframe || avatar.iframe || null;
    const iframeSrc = (typeof iframeHtml === 'string' && iframeHtml.match(/src=["'][^"']+["']/)) ? (iframeHtml.match(/src=["']([^"']+)["']/)[1]) : (typeof iframeHtml === 'string' && iframeHtml.startsWith('http') ? iframeHtml : null);
    if (iframeSrc) {
      setIframeSrcPreview(iframeSrc);
      setVideoOpen(true);
      return;
    }

    // Fallback to original preview behavior (video/audio)
    const url = getAvatarPreviewUrl(avatar);
    if (!url) return;
    // treat video-like urls as videos
    if (String(url).match(/\.mp4|\.webm|\.mov|video/)) {
      setVideoUrl(url);
      setVideoOpen(true);
      return;
    }
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      const audio = new Audio(url);
      audioRef.current = audio;
      const id = avatar.avatar_id || avatar.id || avatar._id || avatar.character_id;
      setPlayingId(id);
      const p = audio.play();
      if (p && p.catch) p.catch(() => setPlayingId(null));
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => setPlayingId(null);
    } catch (err) {
      console.error('play failed', err);
      setSnack({ open: true, message: 'Playback failed', severity: 'error' });
    }
  };

  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  };

  const handleSelect = (avatar) => {
    // open confirmation dialog before saving
    setPendingAvatar(avatar);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    console.log('confirm avatar', pendingAvatar);
    if (!pendingAvatar) return;
    const id = pendingAvatar.avatar_id || pendingAvatar.id || pendingAvatar._id || pendingAvatar.character_id;
    console.log('user org id', userDetails);
    const orgId = routeOrgId || userDetails?.orgId || userDetails?.organizationId || userDetails?.organization?._id;
    if (!orgId) {
      setSnack({ open: true, message: 'Organization not found', severity: 'error' });
      setConfirmOpen(false);
      return;
    }

    setSaving(true);
    try {
      await api.put(`/organization/${orgId}/avatar`, { avatar_id: id }, { headers: { 'x-access-token': userDetails?.jwtToken || '' } });
      // persist selection locally as well
      try { localStorage.setItem('selectedAvatar', JSON.stringify(pendingAvatar)); } catch (e) {}
      setSnack({ open: true, message: 'Avatar saved for future videos', severity: 'success' });
      // update selected id locally so UI reflects selection
      setOrgAvatarId(id);
      setConfirmOpen(false);
      setPendingAvatar(null);
    } catch (e) {
      console.error('save avatar failed', e);
      setSnack({ open: true, message: 'Failed to save avatar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // compute visible avatars based on search/gender filters, and order so selected org avatar appears first
  const visibleAvatars = avatars.filter((a) => {
    const name = ((a.name || a.display_name || a.avatar_name || a.description) || '').toString().toLowerCase();
    if (search && !name.includes(search.toLowerCase())) return false;

    if (genderFilter && genderFilter !== 'all') {
      const raw = ((a.gender || a.voice_gender || (a.attributes && a.attributes.gender)) || '').toString().toLowerCase();
      if (genderFilter === 'other') {
        if (raw === 'male' || raw === 'female' || raw === 'm' || raw === 'f') return false;
      } else if (genderFilter === 'male') {
        if (!(raw === 'male' || raw === 'm')) return false;
      } else if (genderFilter === 'female') {
        if (!(raw === 'female' || raw === 'f')) return false;
      }
    }

    return true;
  });

  let orderedAvatars = visibleAvatars;
  if (orgAvatarId) {
    const selIndex = visibleAvatars.findIndex((a) => {
      const id = a.avatar_id || a.id || a._id || a.character_id;
      return String(id) === String(orgAvatarId);
    });
    if (selIndex > 0) {
      orderedAvatars = [visibleAvatars[selIndex], ...visibleAvatars.slice(0, selIndex), ...visibleAvatars.slice(selIndex + 1)];
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h2" sx={{ mb: 2 }}>Avatars</Typography>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="gender-filter-label">Gender</InputLabel>
          <Select
            labelId="gender-filter-label"
            label="Gender"
            value={genderFilter}
            onChange={(e) => setGenderFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {orderedAvatars.map((a) => {
            const id = a.avatar_id || a.id || a._id || a.character_id;
            const img = getAvatarImageUrl(a);
            const preview = getAvatarPreviewUrl(a);
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={id || JSON.stringify(a)}>
                <Paper sx={{ height: '100%', overflow: 'hidden' }}>
                    {/* original click behavior (kept commented):
                        <Box sx={{ position: 'relative', width: '100%', height: 380, cursor: preview ? 'pointer' : 'default' }} onClick={() => preview && playPreview(a)}>
                    */}
                    {/* Updated: allow iframe thumbnail or image; click opens preview (video or iframe) */}
                    <Box sx={{ position: 'relative', width: '100%', height: 380, cursor: (getAvatarPreviewUrl(a) || a.preview_iframe || a.iframe_embed || a.vimeo_iframe) ? 'pointer' : 'default' }} onClick={() => (getAvatarPreviewUrl(a) || a.preview_iframe || a.iframe_embed || a.vimeo_iframe) && playPreview(a)}>
                    {img ? (
                      <img src={img} alt={a.name || a.display_name || id} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : ( (a.preview_iframe || a.iframe_embed || a.vimeo_iframe || a.iframe) ? (
                      (() => {
                        // derive src if full iframe HTML provided
                        const iframeHtml = a.preview_iframe || a.iframe_embed || a.vimeo_iframe || a.iframe;
                        const srcMatch = (typeof iframeHtml === 'string' && iframeHtml.match(/src=["']([^"']+)["']/));
                        const src = srcMatch ? srcMatch[1] : (typeof iframeHtml === 'string' && iframeHtml.startsWith('http') ? iframeHtml : null);
                        return (
                          <iframe
                            title={a.name || id}
                            src={src ? `${src}${src.includes('?') ? '&' : '?'}autoplay=0` : ''}
                            style={{ width: '100%', height: '100%', border: 0 }}
                            sandbox="allow-scripts allow-same-origin allow-presentation"
                          />
                        );
                      })()
                    ) : (
                      <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.200' }} />
                    ))}
                    {preview && (
                      <IconButton
                        aria-label="play-preview"
                        sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', bgcolor: 'rgba(0,0,0,0.45)', color: 'common.white', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
                        onClick={(e) => { e.stopPropagation(); const playing = playingId === id; if (playing) stopPlayback(); else playPreview(a); }}
                      >
                        {playingId === id ? <PauseIcon fontSize="large" /> : <PlayArrowIcon fontSize="large" />}
                      </IconButton>
                    )}
                  </Box>
                  <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle1">{a.name || a.display_name || a.avatar_name || id}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.gender || ''}</Typography>
                    </Box>
                    <Box>
                      {orgAvatarId && String(orgAvatarId) === String(id) ? (
                        <Button variant="outlined" size="small" disabled>Selected</Button>
                      ) : (
                        <Button variant="contained" size="small" onClick={() => handleSelect(a)}>Select</Button>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        maxWidth="xl"
        fullWidth={false}
        PaperProps={{ sx: { backgroundColor: 'transparent', boxShadow: 'none' } }}
      >
        <DialogContent sx={{ p: 0, bgcolor: 'transparent', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ width: 'min(1200px, 90vw)', aspectRatio: '16 / 9', bgcolor: 'black', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconButton
              onClick={() => setVideoOpen(false)}
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 20, bgcolor: 'rgba(0,0,0,0.45)', color: 'common.white' }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            {/* Updated preview container: responsive 16:9 box */}
            {iframeSrcPreview ? (
              <iframe
                src={`${iframeSrcPreview}${iframeSrcPreview.includes('?') ? '&' : '?'}autoplay=0`}
                style={{ width: '100%', height: '100%', border: 0 }}
                allow="autoplay; fullscreen"
                title="avatar-preview"
              />
            ) : videoUrl && (
              <video
                src={videoUrl}
                controls
                autoPlay
                style={{ width: '100%', height: '100%', objectFit: 'cover', backgroundColor: 'black', display: 'block' }}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog for selecting avatar */}
      <Dialog open={confirmOpen} onClose={() => { if (!saving) { setConfirmOpen(false); setPendingAvatar(null); } }}>
        <DialogTitle>Confirm avatar selection</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>{pendingAvatar ? (pendingAvatar.name || pendingAvatar.display_name || pendingAvatar.avatar_name) : ''}</Typography>
          <Typography>This avatar will be used for all videos created for this organization. Are you sure you want to continue?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmOpen(false); setPendingAvatar(null); }} disabled={saving}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Confirm'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
